import React, { BaseSyntheticEvent, useEffect, useState } from "react";
import { Form, Link, useNavigate, useParams } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { ErrorMessage, Field, FieldArray, Formik } from "formik";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useUserQuery } from "../../graphql/menu.hooks";
import { useFolderShowQuery } from "../../graphql/folder.hooks";
import { usePageQuery } from "../../graphql/page.hooks";
import { useDomainByIdQuery } from "../../graphql/settings.hooks";
import { UserAccess } from "./userAccess";
import {
  useAccessTypeChangeMutation,
  useNewAccessByDomainMutation,
  useNewAccessByFolderMutation,
  useNewAccessByPageMutation,
} from "../../graphql/access.hooks";
import { CustomToast } from "../../components/inputs/toast";
import { FREE_LIMIT, ITEMS } from "../../utils";
import { isUpgradeShow } from "../../graphql/state";
import { LinkIcon } from "./img/LinkIcon";
import { Check } from "react-bootstrap-icons";
import { StyledInput } from "../../components/inputs/styledInput";

export const ShareSettings = () => {
  const lng = useGetUrlLocale();
  const { t } = useTranslation();
  const { item, id } = useParams();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const shared = params.has("Shared");
  const [email, setEmail] = useState<string>("");
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isActiveSave, setIsActiveSave] = useState<boolean>(false);
  const [access, setAccess] = useState(undefined);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const { data: userData, loading: userLoading } = useUserQuery();
  const isPremium = userData?.user.activePremium;
  const [itemData, setItemData] = useState(undefined);

  const { data: domain, refetch: domainRefetch } = useDomainByIdQuery({
    variables: { domainId: +id },
    skip: item !== ITEMS.DOMAIN,
  });

  const { data: folder, refetch: folderRefetch } = useFolderShowQuery({
    variables: { folderId: +id },
    skip: item !== ITEMS.FOLDER,
  });

  const { data: page, refetch: pageRefetch } = usePageQuery({
    variables: { id: +id },
    skip: item !== ITEMS.PAGE,
  });

  const [newAccessByDomain, { loading: newAccessDomainLoading }] =
    useNewAccessByDomainMutation();
  const [newAccessByFolder, { loading: newAccessFolderLoading }] =
    useNewAccessByFolderMutation();
  const [newAccessByPage, { loading: newAccessPageLoading }] =
    useNewAccessByPageMutation();
  const [changeAccessTypes, { loading: changeLoading }] =
    useAccessTypeChangeMutation();

  const domainId = item === ITEMS.DOMAIN ? itemData?.id : itemData?.domain.id;
  const domainTitle =
    item === ITEMS.DOMAIN ? itemData?.title : itemData?.domain.title;
  const isHaveAccesses =
    !domain?.domain.userAccesses.length &&
    !folder?.folderShow.folder.userAccesses.length &&
    !page?.page.userAccesses.length;

  useEffect(() => {
    document.title = "Share Settings";
  }, []);

  useEffect(() => {
    if (ITEMS.DOMAIN === item && domain) {
      setItemData(domain.domain);
      const domainAccess = domain?.domain.userAccesses?.find(
        (user) => user?.user?.email === userData?.user.email
      );
      setAccess(domainAccess);
    } else if (ITEMS.FOLDER === item && folder) {
      setItemData(folder.folderShow.folder);
      const folderAccess = folder?.folderShow.folder.userAccesses?.find(
        (user) => user?.user?.email === userData?.user.email
      );
      setAccess(folderAccess);
    } else if (ITEMS.PAGE === item && page) {
      setItemData(page.page);
      const pageAccess = page?.page.userAccesses?.find(
        (user) => user?.user?.email === userData?.user.email
      );
      setAccess(pageAccess);
    }
  }, [item, id, domain, folder, page, userData]);

  type TInitialsValues = {
    id: number | null;
    accessType: number | null;
    email: string | null;
    userAccesses: any;
  };

  const initialValues: TInitialsValues = {
    id: null,
    accessType: 1,
    email: null,
    userAccesses: itemData?.userAccesses || [],
  };

  const trySaveShareSettings = async (values: TInitialsValues) => {
    const userAccesses = values.userAccesses.map(({ id, accessType }: any) => {
      return { userAccessId: id, typeId: accessType };
    });
    const { data } = await changeAccessTypes({
      variables: { accessTypes: userAccesses },
    });
    if (data.accessTypeChange) {
      if (ITEMS.DOMAIN === item) {
        domainRefetch();
      } else if (ITEMS.FOLDER === item) {
        folderRefetch();
      } else if (ITEMS.PAGE === item) {
        pageRefetch();
      }
      setShowToast(true);
      setIsActiveSave(false);
    }
  };

  const copyLink = () => {
    setIsCopied(true);
    if (item === ITEMS.PAGE) {
      navigator.clipboard.writeText(
        `${process.env.REACT_APP_CONSTRUCTOR_URL}${lng}/${id}`
      );
    } else {
      const path =
        item === ITEMS.DOMAIN
          ? `projects/${id}`
          : `projects/${folder?.folderShow.folder.domain.id}/folder/${folder?.folderShow.folder.id}`;
      navigator.clipboard.writeText(`${window.location.origin}/${path}`);
    }
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  let accessType: number | undefined;
  let accessTypeOwner: string | undefined;

  if (item === ITEMS.DOMAIN) {
    accessType = domain?.domain?.userAccesses[0]?.accessType;
    accessTypeOwner = domain?.domain?.user?.email;
  } else if (item === ITEMS.PAGE) {
    accessType = page?.page?.accessPageFolderDomainByPriority;
    accessTypeOwner = page?.page?.user?.email;
  } else if (item === ITEMS.FOLDER) {
    accessType = folder?.folderShow?.folder?.accessFolderDomainByPriority;
    accessTypeOwner = folder?.folderShow.folder.user?.email;
  } else {
    accessType = undefined;
  }

  const newAccessByItem = async (item: string, accessType: number) => {
    if (
      userData?.user?.countSharedPagesFoldersDomains >= FREE_LIMIT.SHARE &&
      !userData?.user.admin &&
      !isPremium
    ) {
      isUpgradeShow(true);
      return;
    }
    if (ITEMS.DOMAIN === item) {
      const { data } = await newAccessByDomain({
        variables: { domainId: +id, email, accessType: accessType },
      });
      if (data.userAccessDomainAccess) {
        domainRefetch();
        setEmail("");
      }
    } else if (ITEMS.FOLDER === item) {
      const { data } = await newAccessByFolder({
        variables: { folderId: +id, email, accessType: accessType },
      });
      if (data.userAccessFolderAccess) {
        folderRefetch();
        setEmail("");
      }
    } else if (ITEMS.PAGE === item) {
      const { data } = await newAccessByPage({
        variables: { pageId: +id, email, accessType: accessType },
      });
      if (data.userAccessPageAccess) {
        pageRefetch();
        setEmail("");
      }
    }
  };

  const addNewAccess = async (accessType: number) => {
    try {
      await newAccessByItem(item, accessType);
    } catch (err) {
      console.error(err);
    }
  };

  const BackToProject = () => {
    navigate(-1);
  };

  const isNoShared = userData?.user?.domains?.some(
    (domain) => +domain.id === +domainId
  );
  const link = !isNoShared || access ? `${lng}/shared` : `${lng}/projects`;
  const onChangeAccessType = () => {
    setIsActiveSave(true);
  };

  if (userLoading || !itemData) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("profile.loading")}...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="px-lg-3">
      <h1 className="fs-1 fw-bold">{t("share.title")}</h1>
      <div className="text-gray mb-4 mb-md-5">
        <Link to={link}>
          {!isNoShared || access ? t("menu.shared") : t("payment.Projects")}
        </Link>
        <span className="px-2">/</span>
        {itemData && !accessType && !shared && isNoShared && (
          <>
            <Link to={`${lng}/projects/${domainId}`}>{domainTitle}</Link>
            <span className="px-2">/</span>
          </>
        )}
        {folder?.folderShow?.folder?.parentFolder?.title && (
          <>
            <Link
              to={`${lng}/projects/${domainId}/folder/${folder?.folderShow?.folder?.parentFolder?.id}`}
            >
              {folder?.folderShow?.folder?.parentFolder?.title}
            </Link>
            <span className="px-2">/</span>
          </>
        )}
        {t("share.title")}
      </div>
      <div className="text-dark bg-white p-4 py-lg-5 d-flex justify-content-center rounded">
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={trySaveShareSettings}
        >
          {({ handleSubmit, touched, errors, values, setFieldValue }) => (
            <Form className="form-container">
              <div className="d-flex mb-4 flex-column flex-md-row">
                <div className="form-floating position-relative d-flex align-items-center mb-3 mb-md-0 me-md-4 w-100 w-md-75">
                  <Field
                    as="select"
                    name="accessType"
                    className={`form-select text-black d-flex align-items-center p-0 ps-3 pt-3 fs-6`}
                    disabled={
                      accessType === 1 &&
                      userData?.user?.email !== accessTypeOwner
                    }
                  >
                    <option value={1}>{t("share.canView")}</option>
                    <option value={2}>{t("share.canEdit")}</option>
                  </Field>
                  <label
                    htmlFor="options"
                    className="form-label fs-7 m-0 pt-3 ps-3 text-capitalize"
                  >
                    {t("share.accessLink")}
                  </label>
                  <ErrorMessage
                    name="accessType"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
                <button
                  onClick={copyLink}
                  className="copyLinkBtn col-12 col-md-3 btn btn-outline-primary primary-hover d-flex align-items-center justify-content-center"
                >
                  {!isCopied ? (
                    <>
                      <LinkIcon />
                      <p className="m-0 ms-1 fw-normal">
                        {t("share.copyLink")}
                      </p>
                    </>
                  ) : (
                    <>
                      <Check />
                      <p className="m-0 ms-1 fw-normal">{t("share.copied")}</p>
                    </>
                  )}
                </button>
              </div>
              <div className="d-flex mb-4 flex-column flex-md-row">
                <div className="form-floating me-4 mb-3 mb-md-0 me-md-4 w-100 w-md-75">
                  <StyledInput
                    name="email"
                    type="email"
                    label={t("share.addEmail")}
                    error={!!(touched.email && errors.email)}
                    value={email}
                    onChange={(e: BaseSyntheticEvent) =>
                      setEmail(e.target.value)
                    }
                    disabled={
                      accessType === 1 &&
                      userData?.user?.email !== accessTypeOwner
                    }
                  />
                </div>
                <Button
                  disabled={
                    newAccessDomainLoading ||
                    newAccessFolderLoading ||
                    newAccessPageLoading ||
                    !email ||
                    email === userData?.user.email
                  }
                  onClick={() => addNewAccess(+values.accessType)}
                  className="inviteBtn col-12 col-md-3 text-white d-flex align-items-center justify-content-center"
                >
                  <p className="m-0">{t("share.invite")}</p>
                  {(newAccessDomainLoading ||
                    newAccessFolderLoading ||
                    newAccessPageLoading) && (
                    <Spinner animation="border" role="status" className="ms-2">
                      <span className="visually-hidden">
                        {t("profile.loading")}...
                      </span>
                    </Spinner>
                  )}
                </Button>
              </div>
              <ul className="mb-5 p-0 accessList">
                <li
                  className={`d-flex align-items-center justify-content-between ps-3 py-3 ${isHaveAccesses ? "mb-0" : "mb-3"}`}
                >
                  <div className="d-flex align-items-center col-10">
                    <div
                      className="me-3 me-md-4 avatar fs-7 text-white d-flex justify-content-center align-items-center"
                      style={{ height: "26px", width: "26px" }}
                    >
                      {ITEMS.DOMAIN === item && domain?.domain?.user?.email
                        ? domain.domain.user.email[0].toUpperCase()
                        : null}
                      {ITEMS.PAGE === item && page?.page?.user?.email
                        ? page.page.user.email[0].toUpperCase()
                        : null}
                      {ITEMS.FOLDER === item &&
                      folder?.folderShow.folder.user?.email
                        ? folder.folderShow.folder.user.email[0].toUpperCase()
                        : null}
                    </div>
                    <p className="m-0 text-nowrap d-flex align-items-center col-9">
                      {ITEMS.DOMAIN === item && domain?.domain?.user?.email ? (
                        <>
                          <span className="overflow-hidden overflow-ellipsis d-inline-block">
                            {domain.domain.user.email}
                          </span>
                          {domain.domain.user.email === userData?.user?.email
                            ? ` (${t("share.you")})`
                            : ""}
                        </>
                      ) : null}
                      {ITEMS.PAGE === item && page?.page?.user?.email ? (
                        <>
                          <span className="overflow-hidden overflow-ellipsis d-inline-block">
                            {page.page.user.email}
                          </span>
                          {page.page.user.email === userData?.user?.email
                            ? ` (${t("share.you")})`
                            : ""}
                        </>
                      ) : null}
                      {ITEMS.FOLDER === item &&
                      folder?.folderShow?.folder?.user?.email ? (
                        <>
                          <span className="overflow-hidden overflow-ellipsis d-inline-block">
                            {folder?.folderShow.folder.user.email}
                          </span>
                          {folder?.folderShow.folder.user.email ===
                          userData.user?.email
                            ? ` (${t("share.you")})`
                            : ""}
                        </>
                      ) : null}
                    </p>
                  </div>
                  <p className="m-0">{t("share.owner")}</p>
                </li>
                {Boolean(itemData?.userAccesses.length) && (
                  <li className="userAccess__item">
                    <FieldArray name="userAccesses">
                      {() => (
                        <ul className="p-0">
                          {values.userAccesses.map(
                            ({ user, email }: any, index: any) => (
                              <UserAccess
                                key={index}
                                user={user}
                                email={email}
                                index={index}
                                setFieldValue={setFieldValue}
                                accessType={accessType}
                                currentUser={userData?.user?.email}
                                accessTypeOwner={accessTypeOwner}
                                onChangeAccessType={onChangeAccessType}
                              />
                            )
                          )}
                        </ul>
                      )}
                    </FieldArray>
                  </li>
                )}
              </ul>
              <div className="shareSettings d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  className="buttomCol btn btn-outline-dark dark-hover fw-normal col-md-4 me-3 me-md-0"
                  onClick={BackToProject}
                >
                  {t("support.back")}
                </button>
                <button
                  type="button"
                  disabled={changeLoading || !isActiveSave}
                  onClick={() => handleSubmit()}
                  className="buttomCol btn btn-primary col-md-4 text-white"
                >
                  {t("share.save")}
                  {changeLoading && (
                    <Spinner animation="border" role="status" className="ms-2">
                      <span className="visually-hidden">
                        {t("profile.loading")}...
                      </span>
                    </Spinner>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <CustomToast
        isShow={showToast}
        setShow={setShowToast}
        message={`${t("share.beenSaved")}`}
        isSeenClose={false}
        background="bg-success-light"
        text="text-success fw-400 fs-6"
        check={true}
      />
    </div>
  );
};
